/**
 * CooperMembresManagement.js
 * Composant de gestion des membres pour les COOPER'S
 * Utilise le formulaire professionnel MembreCreationForm (copie conforme de DEVENIR MEMBRE)
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

import { Users, UserPlus, RefreshCw, CheckCircle, Clock, Calculator, Clipboard } from 'lucide-react';

import MembreCreationForm from '../MembreCreationForm';
import MembresTable from '../MembresTable';

import FTPEPendingBanner from '../FTPEPendingBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// V37.22 — Parser réutilisable pour l'import depuis presse-papier
const IMPORT_KEY_MAP = {
  'raison sociale': 'raison_sociale',
  'siret': 'siret',
  'prenom': 'prenom',
  'prénom': 'prenom',
  'nom': 'nom',
  'email': 'email',
  'telephone': 'telephone',
  'téléphone': 'telephone',
  'adresse': 'adresse',
  'code postal': 'code_postal',
  'ville': 'ville',
  'territoire': 'territoire',
};

// Champs critiques affichés en chips de validation (ordre = priorité)
const _IMPORT_CRITICAL_FIELDS = [
  { key: 'raison_sociale', label: 'Raison sociale' },
  { key: 'nom', label: 'Nom' },
  { key: 'prenom', label: 'Prénom' },
  { key: 'email', label: 'Email' },
  { key: 'telephone', label: 'Téléphone' },
  { key: 'territoire', label: 'Territoire' },
];

function parseImportText(text) {
  const out = {};
  (text || '').split(/\r?\n/).forEach((line) => {
    const idx = line.indexOf(':');
    if (idx <= 0) return;
    const rawKey = line.slice(0, idx).trim().toLowerCase();
    const value = line.slice(idx + 1).trim();
    if (!value) return;
    const mapped = IMPORT_KEY_MAP[rawKey];
    if (mapped) out[mapped] = value;
  });
  return out;
}

import { useCooperMembres } from './useCooperMembres';
import CooperModals1 from './CooperModals1';
import CooperModals2 from './CooperModals2';
import MemberInvitationIA from '../MemberInvitationIA';

export default function CooperMembresManagement() {
  const ctx = useCooperMembres();
  const { fetchMembres, getAuthHeader, handleViewDocuments, importedInitialValues, loading, membres, setImportText, setImportedInitialValues, setMembres, setSelectedMembre, setShowCreateModal, setShowDetailModal, setShowFTPEModal, setShowImportModal, setShowSimulateurModal, showCreateModal, stats, user } = ctx;
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion de mes Membres</h2>
          <p className="text-gray-600">Créez et gérez les membres que vous accompagnez</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button onClick={fetchMembres} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          {/* V37.20 — Import depuis presse-papier (fiche complète COOPER'S Communityplace) */}
          <Button
            data-testid="import-clipboard-btn"
            size="sm"
            onClick={async () => {
              // Tente de lire automatiquement le presse-papier
              try {
                if (navigator.clipboard?.readText) {
                  const t = await navigator.clipboard.readText();
                  if (t && t.trim()) setImportText(t);
                }
              } catch { /* permission denied — laisser textarea vide */ }
              setShowImportModal(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm border-0"
          >
            <Clipboard className="h-4 w-4 mr-2" />
            Importer depuis presse-papier
          </Button>
          <MemberInvitationIA getAuthHeader={getAuthHeader} onChanged={fetchMembres} userRole={user?.role || 'cooper'} />
          <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <UserPlus className="h-4 w-4 mr-2" />
            Nouveau Membre
          </Button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Membres</p>
                <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Membres Actifs</p>
                <p className="text-3xl font-bold text-green-600">{stats.actifs}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">En Attente</p>
                <p className="text-3xl font-bold text-orange-600">{stats.en_attente}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Estimations</p>
                <p className="text-3xl font-bold text-purple-600">{stats.estimations_envoyees}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Calculator className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Adhésions FTPE en cours (affiché pendant 30 jours) */}
      <FTPEPendingBanner getAuthHeader={getAuthHeader} />

      {/* Liste des membres avec nouveau tableau amélioré */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="h-5 w-5" />
                Mes Membres Adhérents
              </CardTitle>
              <CardDescription className="text-blue-100 mt-1">
                Membres que vous accompagnez dans leurs démarches
              </CardDescription>
            </div>
            <Badge variant="secondary" className="bg-slate-700 text-white border-0 text-lg px-3 py-1 font-semibold shadow-md">
              {membres.length}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          <MembresTable
            membres={membres}
            loading={loading}
            onView={(membre) => {
              setSelectedMembre(membre);
              setShowDetailModal(true);
            }}
            onViewDocuments={handleViewDocuments}
            onCreateEstimation={(membre) => {
              setSelectedMembre(membre);
              setShowSimulateurModal(true);
            }}
            onFTPEAction={(membre) => {
              setSelectedMembre(membre);
              setShowFTPEModal(true);
            }}
            onMemberUpdated={(updatedMembre) => {
              // Mettre à jour la liste locale avec le membre modifié
              setMembres(prev => prev.map(m => 
                m.id === updatedMembre.id ? { ...m, ...updatedMembre } : m
              ));
            }}
            showFTPE={true}
            showRCDS={true}
            showEntreprise={true}
            showExportCSV={false}
            userRole={user?.role || "cooper"}
            onRefresh={fetchMembres}
            onDelete={user?.role === 'super_admin' ? async (membre) => {
              if (!window.confirm(`Supprimer le membre ${membre.prenom || ''} ${membre.nom || ''} ? Cette action est irreversible.`)) return;
              try {
                await axios.delete(`${API}/members/admin/${membre.id}`, { headers: getAuthHeader() });
                toast.success(`Membre ${membre.nom} supprime`);
                setMembres(prev => prev.filter(m => m.id !== membre.id));
              } catch (error) {
                const detail = error.response?.data?.detail;
                toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la suppression');
              }
            } : null}
            emptyMessage="Aucun membre trouvé"
            emptySubMessage="Cliquez sur 'Nouveau Membre' pour créer votre premier membre"
          />
        </CardContent>
      </Card>

      {/* Formulaire de création de membre (composant réutilisable) */}
      <MembreCreationForm
        open={showCreateModal}
        onOpenChange={(o) => {
          setShowCreateModal(o);
          if (!o) setImportedInitialValues(null); // reset au close
        }}
        onSuccess={fetchMembres}
        getAuthHeader={getAuthHeader}
        creatorRole="cooper"
        initialValues={importedInitialValues}
      />
      <CooperModals1 ctx={ctx} />
      <CooperModals2 ctx={ctx} />
    </div>
  );
}
